// clipboard.js — copy a visible value (order number, tracking number) to the
// clipboard. Spec: the 2026-10-07 BO orders status-tabs design, §12.4–§12.5.
//
// Vue-free so it is testable with a fake env; `copyToClipboard` NEVER rejects:
// navigator.clipboard.writeText first (needs https / permission), then the
// old textarea + document.execCommand("copy") fallback (works on http).

/** Exactly what the cell shows: null/undefined → '', otherwise String(v).trim(). */
export function clipboardText(value) {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

function defaultEnv() {
  return {
    navigator: typeof globalThis !== 'undefined' ? globalThis.navigator : undefined,
    document: typeof globalThis !== 'undefined' ? globalThis.document : undefined
  };
}

function execCommandCopy(doc, text) {
  if (!doc || typeof doc.createElement !== 'function' || typeof doc.execCommand !== 'function' || !doc.body) {
    return false;
  }
  let ta = null;
  try {
    ta = doc.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    ta.style.top = '0';
    ta.style.left = '0';
    doc.body.appendChild(ta);
    ta.select();
    return !!doc.execCommand('copy');
  } catch (e) {
    return false;
  } finally {
    try {
      if (ta && ta.parentNode) ta.parentNode.removeChild(ta);
    } catch (e) {
      // ignore
    }
  }
}

/** → Promise<boolean>; empty (after clipboardText) → false without touching env. */
export async function copyToClipboard(text, env) {
  const value = clipboardText(text);
  if (!value) return false;
  const e = env || defaultEnv();
  try {
    const nav = e.navigator;
    if (nav && nav.clipboard && typeof nav.clipboard.writeText === 'function') {
      await nav.clipboard.writeText(value);
      return true;
    }
  } catch (err) {
    // denied / insecure context — fall through to execCommand
  }
  return execCommandCopy(e.document, value);
}

export default { clipboardText, copyToClipboard };
